<template>
  <MediaTabsBlock
    :items="tabItems"
    :tabs-positon="item.fields?.tabPosition ?? 'bottom'"
    :title="item.fields?.title"
    :tab-list-horizontal-width="tabWidth"
    :state="externalState"
    v-bind="
      ContentfulLivePreview.getProps({
        entryId: item.sys.id,
        fieldId: 'title'
      })
    "
    @tab-change="onTabChange"
  />
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { TabsMediaBlock as MediaTabsBlock } from '@hypercodestudio/basler-components';
import type { IMediaTabItem, IMediaTabs } from '~/lib/ContentfulService';
import { useMedia } from '~/composables/useMedia';
import { isEntryResolveError } from '~/utils/guards/isEntryResolveError';
import type { TabData } from '@hypercodestudio/basler-components/dist/models/tabs';
import { isDefined } from '~/utils/guards/isDefined';
import type { MediaItemInterface } from '@hypercodestudio/basler-components/dist/components/elements/media/Media.vue';
import { ContentfulLivePreview } from '@contentful/live-preview';

interface Props {
  item: IMediaTabs;
}

const QUERY_PARAM_ACTIVE_TABS = '_aTabs';

const props = defineProps<Props>();

const router = useRouter();
const route = useRoute();

const tabWidth = computed(() =>
  props.item.fields?.tabWidth === '46.5' ? 46.5 : undefined
);

const iMediaTabItems: IMediaTabItem[] = props.item.fields?.tabItems?.filter(
  (entry) => !isEntryResolveError(entry)
);

const tabIds = computed(() =>
  iMediaTabItems.map((item) => item.fields?.id ?? item.sys.id)
);

const activeTabId = ref<string | undefined>(
  getActiveTabsFromQuery().filter((idOrUid) =>
    tabIds.value.includes(idOrUid)
  )[0]
);

const media: Record<string, MediaItemInterface> = Object.assign(
  {},
  ...(
    await Promise.all(
      iMediaTabItems.map(async (item) => {
        const mediaItemInterface = await useMedia(item.fields.media);
        if (!mediaItemInterface) {
          return undefined;
        }

        return {
          [item.fields.media.sys.id]:
            mediaItemInterface satisfies MediaItemInterface
        };
      })
    )
  ).filter(isDefined)
);

const tabItems = computed(() =>
  iMediaTabItems.map((item, index) => {
    const idOrUid = item.fields?.id ?? item.sys.id;

    return {
      uid: idOrUid,
      media: media[item.fields.media.sys.id],
      title: item.fields.title,
      activeOnInit: activeTabId.value ? activeTabId.value == idOrUid : !index,
      imgSrc: item.fields.image?.fields?.image?.fields?.file?.url,
      subtitle: item.fields.subtitle,
      icon: item.fields?.tabIcon
        ? {
            name: item.fields?.tabIcon,
            scaleFactor: 2.5
          }
        : ''
    };
  })
);

const externalState = reactive({
  title: props.item.fields?.title,
  tabs: []
});

function getActiveTabsFromQuery(): string[] {
  return route.query &&
    QUERY_PARAM_ACTIVE_TABS in route.query &&
    isDefined(route.query[QUERY_PARAM_ACTIVE_TABS]) &&
    route.query[QUERY_PARAM_ACTIVE_TABS].length > 0
    ? route.query[QUERY_PARAM_ACTIVE_TABS].toString().split(',')
    : [];
}

const onTabChange = async (tab: TabData) => {
  await setActiveTab(
    tab.computedId,
    getActiveTabsFromQuery().filter((uid) => uid !== activeTabId.value)
  );
};

async function setActiveTab(
  uid: string,
  currentActiveTabs: string[] = getActiveTabsFromQuery()
) {
  if (uid === activeTabId.value) {
    return;
  }

  activeTabId.value = uid;
  externalState.tabs.forEach(
    (tab: { isActive: boolean; computedId: string }) => {
      tab.isActive = tab.computedId === activeTabId.value;
    }
  );

  currentActiveTabs.push(uid);

  await router.replace({
    query: {
      ...route.query,
      [QUERY_PARAM_ACTIVE_TABS]: currentActiveTabs.join(',')
    },
    hash: route.hash
  });
}

watch(
  () => route.query,
  () => {
    if (route.query[QUERY_PARAM_ACTIVE_TABS]) {
      const shouldActiveTabUid = getActiveTabsFromQuery().filter((uid) =>
        tabIds.value.includes(uid)
      )[0];

      if (
        isDefined(shouldActiveTabUid) &&
        activeTabId.value !== shouldActiveTabUid
      ) {
        setActiveTab(
          shouldActiveTabUid,
          getActiveTabsFromQuery().filter((uid) => uid !== shouldActiveTabUid)
        );
      }
    }
  }
);
</script>
